import type { Spot, Fault, Constraint } from "./types.js";
/**
 * FIVB 2025-2028 Rule 7.4.3, as executable code.
 *
 * At the moment of the service hit the receiving team must be in rotational
 * order. The rule is narrower than beginners expect: it binds each player only
 * to their immediate neighbours, never to a diagonal, and "level with" is
 * explicitly legal.
 *
 *   7.4.3.1  each back-row player must be level with or have at least part of
 *            one foot further from the centre line than the front foot of the
 *            corresponding front-row player
 *   7.4.3.2  each right (left) side player must be level with or have at least
 *            part of one foot closer to the right (left) sideline than the foot
 *            further from that sideline of the other players in that row
 *
 * Note that Rule 7.4 binds the RECEIVING team only: "The players of the serving
 * team, however, are free to occupy any position at the service hit." Callers
 * decide when to apply this; the validator itself just answers the question.
 */

/** Front-row zone paired with the back-row zone behind it (Rule 7.4.3.1). */
const FRONT_BACK = [
  [4, 5],
  [3, 6],
  [2, 1],
];

/** Left zone paired with the zone immediately to its right (Rule 7.4.3.2). */
const LATERAL = [
  [4, 3],
  [3, 2],
  [5, 6],
  [6, 1],
];

/**
 * Tolerance in metres. The rule allows a player to be "level with" a neighbour,
 * so a strict inequality would report faults for legal ties.
 */
const EPSILON = 0.02;

type ZoneSpots = Map<number, Spot | undefined> | Partial<Record<number, Spot>>;
const spotOf = (spots: ZoneSpots, zone: number) =>
  spots instanceof Map ? spots.get(zone) : spots[zone];

/**
 * Every positional fault in a set of zone spots.
 *
 * @param spots  zone number -> `{ x, z }` in court metres, as a Map or object
 * @returns      `{ rule, zones }` for each broken pair; empty when legal
 */
export function findFaults(spots: ZoneSpots) {
  const faults: Fault[] = [];

  for (const [front, back] of FRONT_BACK) {
    const f = spotOf(spots, front);
    const b = spotOf(spots, back);
    if (!f || !b) continue;
    if (b.z < f.z - EPSILON) {
      faults.push({ rule: "7.4.3.1", zones: [front, back] });
    }
  }

  for (const [left, right] of LATERAL) {
    const l = spotOf(spots, left);
    const r = spotOf(spots, right);
    if (!l || !r) continue;
    if (l.x > r.x + EPSILON) {
      faults.push({ rule: "7.4.3.2", zones: [left, right] });
    }
  }

  return faults;
}

/**
 * The constraints acting on one zone, phrased from that zone's point of view so
 * the UI can render them as instructions to the selected player.
 *
 * `requirement` is what the selected player must do relative to `other`:
 * `nearerNetThan`, `behind`, `leftOf` or `rightOf`.
 */
export function constraintsFor(zone: number) {
  const constraints: Constraint[] = [];

  for (const [front, back] of FRONT_BACK) {
    if (zone === front) {
      constraints.push({ rule: "7.4.3.1", other: back, requirement: "nearerNetThan" });
    } else if (zone === back) {
      constraints.push({ rule: "7.4.3.1", other: front, requirement: "behind" });
    }
  }

  for (const [left, right] of LATERAL) {
    if (zone === left) {
      constraints.push({ rule: "7.4.3.2", other: right, requirement: "leftOf" });
    } else if (zone === right) {
      constraints.push({ rule: "7.4.3.2", other: left, requirement: "rightOf" });
    }
  }

  return constraints;
}
